import { Input } from 'antd'
import { createRoot } from 'react-dom/client'
import {
  HOVER_EDGE_TOOL_CLASS,
  RED,
  SOURCE_ARROWHEAD_STROKE_WIDTH,
  TARGET_ARROWHEAD_STROKE_WIDTH,
} from '@/assets/constant'
import sourceAnchorCursor from '@/assets/source-anchor-cursor.png'
import { DROP_SHADOW_FILTER, OUTLINE_COLOR } from '@/assets/x6Model'
import { AddBlockCommand } from '@/components/AddBlockCommand'
import {
  BlockParamWindow,
  SubsystemParamWindow,
} from '@/components/NodeParamWindow'
import { hasSubsystemMask } from '@/services/subsystem-service'
import { focusOrRestoreFloatingWindow } from '@/store/floatingWindowStore'
import { useGraphStore } from '@/store/graphStore'
import { useSubGraphStore } from '@/store/subGraphStore'
import { getGraphInteractionAdapter } from '@/touch/service/graph-interaction-adapter-service'
import type { NodeParamWindowTarget } from '@/components/NodeParamWindow'
import type { Cell, Edge, Graph, Node, NodeProperties } from '@antv/x6'
import type { ScaleContentToFitOptions } from '@antv/x6'
import type { Block } from '~/types/vo/block'

let activeAddBlockCommandDestroy: (() => void) | null = null

// ── Label 就地编辑器（antd Input 浮层）──────────────────────────────────────

/**
 * 双击节点 label 时弹出的浮动输入框。
 * 读取 node.attr('label/text') 作为初始值，失焦/回车写回，Escape 取消。
 */
function LabelEditor({
  node,
  rect,
  onDestroy,
}: {
  node: Node
  rect: DOMRect
  onDestroy: () => void
}) {
  const initialValue = node.attr<string>('label/text') ?? ''
  const [value, setValue] = useState(initialValue)

  const commit = () => {
    const trimmed = value.trim()
    if (trimmed !== initialValue) {
      node.attr('label/text', trimmed)
    }
    onDestroy()
  }

  return (
    <div
      style={{
        position: 'fixed',
        left: rect.left,
        top: rect.top,
        width: Math.max(rect.width, 80),
        zIndex: 9999,
      }}
    >
      <Input
        size="small"
        value={value}
        autoFocus
        onFocus={(e) => e.target.select()}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onPressEnter={commit}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onDestroy()
        }}
      />
    </div>
  )
}

function createInteractiveService() {
  function getFilterWidth(width: number) {
    const zoom = useGraphStore.getState().graph?.zoom() || 1
    return Number((width / zoom).toFixed(3))
  }

  function addOutline(cell: Cell) {
    if (cell.isNode()) {
      if (cell.getData()?.blockType === 'Annotation') {
        cell.attr(
          {
            body: {
              fill: OUTLINE_COLOR,
              fillOpacity: 1,
              filter: null,
            },
          },
          { undo: false },
        )
        return
      }
      cell.attr('body/filter', null, { undo: false })
      cell.attr(
        'body/filter',
        {
          name: 'outline',
          args: {
            color: OUTLINE_COLOR,
            width: Math.min(getFilterWidth(4), 4),
            margin: 0,
          },
        },
        { undo: false },
      )
    } else if (cell.isEdge()) {
      const adapter = getGraphInteractionAdapter()
      if (adapter) {
        adapter.addEdgeOutline(cell, { getFilterWidth })
        return
      }
      cell.attr(
        'line/filter',
        {
          name: 'outline',
          args: {
            color: OUTLINE_COLOR,
            width: Math.min(getFilterWidth(3), 3),
            margin: 0,
          },
          attrs: {
            filterUnits: 'userSpaceOnUse',
            x: -9999,
            y: -9999,
            width: 19998,
            height: 19998,
          },
        },
        { undo: false },
      )
    }
  }
  /**
   * @add 取消节点outline添加阴影
   * @param cell 处理的 Cell
   */
  function removeOutline(cell: Cell) {
    if (cell.isNode() && cell.getData()?.blockType === 'Annotation') {
      cell.attr(
        {
          body: {
            fill: '#ffffff',
            fillOpacity: 0,
            filter: null,
          },
        },
        { undo: false },
      )
    } else if (cell.isNode())
      cell.attr('body/filter', DROP_SHADOW_FILTER, { undo: false })
    else if (cell.isEdge()) {
      const adapter = getGraphInteractionAdapter()
      if (adapter) {
        adapter.removeEdgeOutline(cell)
        return
      }
      cell.attr('line/filter', null, { undo: false })
    }
  }

  /**
   * @param cell 目标元素
   * @description 在节点上添加边界工具，不加入undoStack
   */
  function addBoundaryTool(cell: Cell) {
    cell.addTools(
      {
        name: 'boundary',
        args: {
          padding: 5,
          attrs: {
            fill: '#7c68fc',
            stroke: '#333',
            strokeWidth: 0.5,
            fillOpacity: 0.2,
          },
        },
      },
      { undo: false },
    )
  }

  function initializeEdgeTools(edge: Edge) {
    const graph = useGraphStore.getState().graph
    const isPreview = edge.getAttrs()?.line?.stroke === RED

    const sourceCell = graph.getCellById(edge.getSourceCellId())
    const isBranchEdge = sourceCell?.isEdge()
    const adapter = getGraphInteractionAdapter()
    if (adapter) {
      adapter.initializeEdgeTools(edge, { graph, isPreview, isBranchEdge })
      return
    }

    const managedToolNames = [
      'ratio-anchor',
      'source-arrowhead',
      'target-arrowhead',
      'touch-source-arrowhead',
      'touch-target-arrowhead',
    ]
    const tools = edge.getTools()
    const persistentTools =
      tools?.items.filter((item) => {
        const name = typeof item === 'string' ? item : item.name
        return !managedToolNames.includes(name)
      }) ?? []
    const hoverTools = []
    if (isBranchEdge) {
      hoverTools.push({
        name: 'ratio-anchor',
        args: { className: HOVER_EDGE_TOOL_CLASS },
      })
    } else {
      hoverTools.push(
        {
          name: 'source-arrowhead',
          args: {
            className: HOVER_EDGE_TOOL_CLASS,
            attrs: {
              d: 'M -7.5 -7.5 H 7.5 V 7.5 H -7.5 Z',
              fill: 'transparent',
              stroke: 'transparent',
              cursor: `url("${sourceAnchorCursor}") 16 16, default`,
              'stroke-width': SOURCE_ARROWHEAD_STROKE_WIDTH,
            },
          },
        },
        // { name: 'vertices' },
        // { name: 'segments' },
      )
    }
    hoverTools.push({
      name: 'target-arrowhead',
      args: {
        className: HOVER_EDGE_TOOL_CLASS,
        // ratio: isPreview ? 1 : 1,
        attrs: {
          // 使用 d 反转箭头 防止嵌入 Block 造成预期行为错乱
          ...(isPreview ? {} : { d: 'M 0 -8 -18 0 0 8 Z' }),
          fill: 'transparent',
          stroke: 'transparent',
          'stroke-width': TARGET_ARROWHEAD_STROKE_WIDTH,
          cursor: 'move',
        },
      },
    })
    const currentNames = tools?.items.map((item) =>
      typeof item === 'string' ? item : item.name,
    )
    const expectedNames = hoverTools.map((item) => item.name)
    const alreadyInitialized =
      currentNames?.filter((name) => managedToolNames.includes(name)).join() ===
      expectedNames.join()
    if (alreadyInitialized) return

    edge.setTools(
      { ...tools, items: [...persistentTools, ...hoverTools] },
      { undo: false },
    )
  }

  /**
   * 统一节点参数悬浮窗口入口。
   * - 已封装子系统 → SubsystemParamWindow（读取 maskParam）
   * - 其他 block → BlockParamWindow（读取 paramValues）
   * 注：子系统未封装时由 useGraphListener 直接进入子系统，不经过此方法
   */
  function openNodeParamWindow(node: Node) {
    const windowId = `node-param:${node.id}`
    if (focusOrRestoreFloatingWindow(windowId)) return
    const target: NodeParamWindowTarget = {
      graphId: useSubGraphStore.getState().currentGraphId,
      nodeId: node.id,
      snapshot: node.toJSON() as NodeProperties,
    }

    const ParamWindow = hasSubsystemMask(node)
      ? SubsystemParamWindow
      : BlockParamWindow

    const container = document.createElement('div')
    document.body.appendChild(container)
    const root = createRoot(container)

    const destroy = () => {
      requestAnimationFrame(() => {
        root.unmount()
        container.remove()
      })
    }

    root.render(
      <ParamWindow windowId={windowId} target={target} onDestroy={destroy} />,
    )
  }

  /**
   * 双击节点 label 文本时，在文本位置浮动 antd Input 就地编辑。
   * 失焦/回车写回 node.attr('label/text')，Escape 取消。
   */
  function openLabelEditor(node: Node, textEl: Element) {
    const rect = textEl.getBoundingClientRect()

    const container = document.createElement('div')
    document.body.appendChild(container)
    const root = createRoot(container)

    const destroy = () => {
      requestAnimationFrame(() => {
        root.unmount()
        container.remove()
      })
    }

    root.render(<LabelEditor node={node} rect={rect} onDestroy={destroy} />)
  }

  /**
   * 根据 Block 元数据创建节点并添加到画布指定位置（居中于点击点）
   * 复用 stencil getDragNode 的后处理逻辑：尺寸兜底、阴影
   */
  function addNodeFromBlock(block: Block, x: number, y: number): void {
    const graph = useGraphStore.getState().graph
    if (!graph) return

    const node = graph.createNode(block)

    // 阴影（新节点默认外观）
    removeOutline(node)

    // 子系统不做端口/尺寸处理，方便解构
    if (node.getData()?.blockType !== 'Subsystem') {
      // 宽高相等时设为最小 60×60
      const { width, height } = node.getSize()
      if (width === height) {
        node.size(Math.max(60, width), Math.max(60, height))
      }
    }

    // 居中于双击位置
    const { width, height } = node.getSize()
    node.setPosition(x - width / 2, y - height / 2)

    graph.addNode(node)
  }

  /**
   * 命令式打开"添加模块"浮动面板，双击画布空白处触发
   * @param graphX 画布坐标 X（用于节点放置）
   * @param graphY 画布坐标 Y（用于节点放置）
   * @param screenX 屏幕坐标 X（用于面板定位，clientX）
   * @param screenY 屏幕坐标 Y（用于面板定位，clientY）
   */
  function openAddBlockCommand(
    graphX: number,
    graphY: number,
    screenX: number,
    screenY: number,
  ) {
    activeAddBlockCommandDestroy?.()
    const container = document.createElement('div')
    document.body.appendChild(container)
    const root = createRoot(container)

    let destroyed = false
    const destroy = () => {
      if (destroyed) return
      destroyed = true
      if (activeAddBlockCommandDestroy === destroy)
        activeAddBlockCommandDestroy = null
      container.remove()
      requestAnimationFrame(() => {
        root.unmount()
      })
    }
    activeAddBlockCommandDestroy = destroy

    root.render(
      <AddBlockCommand
        screenX={screenX}
        screenY={screenY}
        onDestroy={destroy}
        onSelect={(block) => addNodeFromBlock(block, graphX, graphY)}
      />,
    )
  }

  /**
   * zoomToFit with virtual render support.
   * Temporarily disables virtual rendering & async to ensure all cell views are
   * in the DOM so useCellGeometry:false can read the full visual bounding box.
   */
  function zoomToFitWithVirtual(
    graph: Graph,
    options?: ScaleContentToFitOptions,
  ) {
    // 兼容非开启 virtual
    if (!graph.options.virtual) {
      graph.zoomToFit({
        useCellGeometry: false,
        ...options,
      })
      return
    }
    graph.options.async = false
    graph.disableVirtualRender()
    ;(graph as any).renderer.schedule.renderViews(graph.getCells())
    graph.zoomToFit({
      useCellGeometry: false,
      ...options,
    })
    graph.options.async = true
    graph.enableVirtualRender()
  }

  return {
    addOutline,
    removeOutline,
    addBoundaryTool,
    initializeEdgeTools,
    openNodeParamWindow,
    openLabelEditor,
    openAddBlockCommand,
    addNodeFromBlock,
    zoomToFitWithVirtual,
  }
}

export { createInteractiveService }
